import { computed, reactive, ref } from 'vue';
import { fetchCampaignForm } from '../data/fetchCampaignForm';
import { submitCampaign } from '../data/submitCampaign';
import { buildCampaignPayload } from '../utils/buildCampaignPayload';
import { getCampaignDetails } from '../services/getCampaignDetails';
let uniqueSequence = 0;
const createId = () => `ce_${Date.now()}_${uniqueSequence++}`;
const createCondition = () => ({
    id: createId(),
    fieldId: null,
    categoryId: null,
    operator: null,
    values: [],
});
const createGroup = () => ({
    id: createId(),
    conditions: [createCondition()],
});
// Função para detectar se o template é HTML importado ou criado no builder
const isHtmlTemplate = (template) => {
    if (!template || template.trim().length === 0)
        return false;
    const htmlPattern = /<html[^>]*>|<body[^>]*>|<!DOCTYPE/i;
    return htmlPattern.test(template);
};
export const useCampaignEmailForm = () => {
    const state = reactive({
        loading: true,
        loadingTemplateHtml: false,
        saving: false,
        schema: [],
        query: {
            include: [createGroup()],
            exclude: [],
        },
        settings: {
            name: '',
            audienceId: null,
            title: '',
            preheader: '',
            senderName: '',
            senderEmail: '',
            replyToEmail: '',
            categoryId: null,
            ipPoolName: '',
            creationType: null,
            template: '',
            scheduleDate: null,
            scheduleTime: null,
            isScheduled: false,
            isRecurrent: false,
            notes: '',
            scheduleAt: '',
        },
        errors: [],
        lastSavedId: null,
    });
    const schemaByField = ref(new Map());
    const hydrateSchemaIndex = (groups) => {
        const index = new Map();
        groups.forEach(group => {
            group.fields.forEach(field => {
                index.set(`${group.id}::${field.id}`, field);
            });
        });
        schemaByField.value = index;
    };
    const load = async () => {
        state.loading = true;
        try {
            const schema = await fetchCampaignForm();
            state.schema = schema.groups;
            hydrateSchemaIndex(schema.groups);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : '';
            state.errors = message ? [message] : [];
        }
        finally {
            state.loading = false;
        }
    };
    const loadCampaign = async (id) => {
        state.loadingTemplateHtml = true;
        try {
            const campaign = await getCampaignDetails(id);
            const templateContent = campaign.template;
            const isHtml = isHtmlTemplate(templateContent);
            let creationType = campaign.creation_type || null;
            if (creationType === 'html' || creationType === 'builder') {
                creationType = null;
            }
            if (!creationType && templateContent && isHtml) {
                creationType = null;
            }
            const audienceId = campaign.audience?.id || campaign.audience_id;
            const categoryId = campaign.category?.id || campaign.category_id;
            let scheduleDate = campaign.schedule_date || null;
            let scheduleTime = campaign.schedule_time || null;
            if (campaign.scheduled_at) {
                const [date, time] = campaign.scheduled_at.split(' ');
                scheduleDate = date || null;
                scheduleTime = time ? time.substring(0, 5) : null; // Remove segundos
            }
            state.settings.name = campaign.name || '';
            state.settings.audienceId = audienceId != null ? Number(audienceId) : 0;
            state.settings.title = campaign.title || campaign.subject || '';
            state.settings.preheader = campaign.preheader || campaign.preheader_text || '';
            state.settings.senderName = campaign.sender_name || '';
            state.settings.senderEmail = campaign.sender_email || '';
            state.settings.replyToEmail = campaign.reply_to_email || '';
            state.settings.categoryId = categoryId ? Number(categoryId) : null;
            state.settings.ipPoolName = campaign.ip_pool_name || null;
            state.settings.creationType = creationType;
            state.settings.template = templateContent;
            state.settings.scheduleDate = scheduleDate;
            state.settings.scheduleTime = scheduleTime;
            state.settings.isScheduled = campaign.is_scheduled || false;
            state.settings.isRecurrent = campaign.is_recurrent || false;
            state.settings.notes = campaign.notes || '';
            state.settings.scheduleAt = campaign.schedule_at || campaign.scheduled_at || '';
            return {
                audience: campaign.audience || null,
                category: campaign.category || null,
            };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : '';
            state.errors = message ? [message] : [];
            throw error;
        }
        finally {
            state.loading = false;
        }
    };
    const findField = (categoryId, fieldId) => {
        if (!categoryId || !fieldId)
            return null;
        return schemaByField.value.get(`${categoryId}::${fieldId}`) ?? null;
    };
    const updateCondition = (groupType, groupId, conditionId, patch) => {
        const groups = state.query[groupType];
        const group = groups.find(item => item.id === groupId);
        if (!group)
            return;
        const condition = group.conditions.find(item => item.id === conditionId);
        if (!condition)
            return;
        Object.assign(condition, patch);
    };
    const setField = (groupType, groupId, conditionId, categoryId, fieldId) => {
        const descriptor = findField(categoryId, fieldId);
        updateCondition(groupType, groupId, conditionId, {
            categoryId,
            fieldId,
            operator: descriptor?.operators[0] ?? null,
            values: [],
        });
    };
    const setOperator = (groupType, groupId, conditionId, operator) => {
        updateCondition(groupType, groupId, conditionId, { operator });
    };
    const setValues = (groupType, groupId, conditionId, values) => {
        updateCondition(groupType, groupId, conditionId, { values });
    };
    const addGroup = (groupType) => {
        state.query[groupType].push(createGroup());
    };
    const removeGroup = (groupType, groupId) => {
        state.query[groupType] = state.query[groupType].filter(group => group.id !== groupId);
        if (!state.query[groupType].length && groupType === 'include') {
            state.query[groupType].push(createGroup());
        }
    };
    const addCondition = (groupType, groupId) => {
        const group = state.query[groupType].find(item => item.id === groupId);
        if (!group)
            return;
        group.conditions.push(createCondition());
    };
    const removeCondition = (groupType, groupId, conditionId) => {
        const group = state.query[groupType].find(item => item.id === groupId);
        if (!group)
            return;
        group.conditions = group.conditions.filter(condition => condition.id !== conditionId);
        if (!group.conditions.length) {
            group.conditions.push(createCondition());
        }
    };
    const canSave = computed(() => {
        // Validação dos campos obrigatórios do formulário
        const hasBasicFields = !!(state.settings.name &&
            state.settings.audienceId &&
            state.settings.title &&
            state.settings.senderName &&
            state.settings.senderEmail &&
            state.settings.categoryId &&
            state.settings.ipPoolName &&
            state.settings.template);
        // Se agendado, precisa ter data e hora
        if (state.settings.isScheduled) {
            return hasBasicFields && !!(state.settings.scheduleDate && state.settings.scheduleTime);
        }
        return hasBasicFields;
    });
    const save = async () => {
        state.errors = [];
        state.saving = true;
        try {
            const payload = buildCampaignPayload(state);
            const response = await submitCampaign(payload);
            const responseData = response?.data;
            state.lastSavedId = responseData?.id ?? responseData?.data?.id ?? null;
            return response;
        }
        catch (error) {
            const message = error instanceof Error ? error.message : '';
            state.errors = message ? [message] : [];
            throw error;
        }
        finally {
            state.saving = false;
        }
    };
    return {
        state,
        schemaByField,
        load,
        loadCampaign,
        addGroup,
        removeGroup,
        addCondition,
        removeCondition,
        setField,
        setOperator,
        setValues,
        updateCondition,
        canSave,
        save,
        findField,
    };
};
